// Contrôleur solo : enchaîne les niveaux, gère les vies, les morts, la victoire,
// et peint les écrans de transition par-dessus le rendu du jeu. La simulation est
// gelée (state.finished) pendant les phases non jouables, si bien que la dernière
// image reste figée sous le panneau.
import { CatalogEntry } from '../shared/levels-catalog'
import { closeEmoteWheel, handleEmoteKey, stopEmote } from './emote-ui'
import { type DecorGrid, decorGrid } from '../shared/decor'
import { FAST_FINISH_MS, ZERO_COUNTERS, type QuestCounters } from '../shared/economy'
import { levelFromAscii } from '../shared/level'
import { createGame, GameEvent, GameState } from '../shared/state'
import { PlayerInput, SUB } from '../shared/types'
import { createKeyboard } from './input'
import { equipped } from './loadout'
import { LoopHandle, startLoop } from './loop'
import { isTouch } from './platform'
import { bumpShake, DIG_ANIM_TICKS, drawTazeFlashes, FLASH_MS, posEcranCoureur, render, renderHud, RenderCtx, setRunnerLooks, TazeFlash, TS } from './render'
import { ENTREE_MS, entreeDessinee, peintEntree } from './entree-scene'
import { isSolid } from '../shared/physics'
import { estImmerge } from '../shared/sim'
import { ROWS } from '../shared/types'
import { avatarHue } from './prefs'
import { hueIndex } from '../shared/skins'
import { drawBanner, drawCenterScreen } from './screens'
import { createTouchControls } from './touch'
import { formatTime } from './format-time'
import { victoireFx } from './victoire-fx'
import { apexPing, isMuted, playEvents, sonRepere, startAmbiance, startMusic, stepSound, stopAmbiance, stopMusic, toggleMute } from './audio'
import { bounceVariant } from './bounce-variant'
import { dashPref, ghostPref, ghostRival, torchPref } from './prefs'
import { declencheHitstop, hitstopActif, HITSTOP_MS, resetHitstop } from './hitstop'
import { SERIE_MIN, SERIE_TICKS, SERIE_VIDE, type Serie, serieSuivante, serieVisible } from './serie'
import { CHASSE_VIDE, type Chasse, chasseSuivante, vientDeReperer } from './chasse'
import { monkMood } from '../shared/monks'
import { causeLabel, lastDeathCause, type DeathCause } from './death'
import { apiGhost, apiPutGhost, apiQuestProgress } from './api'
import { showDailyToasts, showQuestToasts, showToast } from './economy-ui'
import { GhostRecorder, GhostRun } from './ghost'
import { encodeInputs } from '../shared/ghost'
import { avanceCombo, FENETRE_VIERGE, type FenetreCombo, tallyEvents } from './quest-count'

type Phase = 'playing' | 'paused' | 'dying' | 'reviving' | 'won-level' | 'game-over' | 'all-done'
const LIVES = 5, DYING_MS = 1000, WON_MS = 2000, END_MS = 3000, FADE_TICKS = 12

export interface SoloOpts {
  canvas: HTMLCanvasElement
  sheet: HTMLImageElement
  levels: CatalogEntry[]
  startIndex: number
  onQuit: () => void
  // Appelé une fois par niveau réussi, avec le temps figé (ms), le nombre de morts sur ce
  // niveau et le MODE de la partie (records par mode, 16/07) : dash = celui de la SIMULATION
  // jouée (state.dashEnabled, pas la pref au moment de l'envoi), torche = réglage visuel au
  // moment de la victoire. « Tirer et oublier » : main.ts y branche l'envoi du score (jamais
  // bloquant, uniquement si connecté) ; absent = solo hors ligne. solo reste découplé du réseau.
  onLevelWon?: (slug: string, timeMs: number, deaths: number, mode: { dash: boolean; torch: boolean }) => void
  // Enchaînement à la victoire (20/07, demande Claude-Alain « enchaîner un niveau en speed-run ») :
  // true (défaut) = à la victoire on passe AUTOMATIQUEMENT au niveau suivant après un court écran
  // (campagnes : le parcours doit s'enchaîner). false = free-play : l'écran de victoire ATTEND un
  // choix -> R/🔄 rejoue le MÊME niveau (speed-run enchaînable), →/Entrée/tap passe au suivant.
  // Seuls les niveaux de test (startGame) passent false ; campagnes/éditeur/famille gardent true.
  autoAdvance?: boolean
  // ENTRÉE EN SCÈNE (01/08) : l'identifiant de l'animation d'arrivée à jouer au début de CHAQUE
  // NIVEAU (shared/entrees.ts). Absente = le jeu démarre comme avant, sans une image de plus.
  // ⚠️ Elle ne rejoue PAS après une mort : elle salue une arrivée, pas une tentative. Un joueur
  // qui recommence vingt fois la même carte la verrait sinon vingt fois.
  entree?: string
}
export interface SoloHandle { stop(): void }

// Effet de VICTOIRE cosmétique (Lot C T5, loot `victory`) : peint PAR-DESSUS l'écran « NIVEAU
// RÉUSSI ! » (ADDITIF, ne le remplace pas). `victoire-feu` = fusées qui éclatent en gerbes colorées
// dans le haut de l'aire ; `victoire-pluie` = pluie de pièces d'or en fines traînées. Entièrement
// DÉTERMINISTE de `ageMs` (aucun Math.random par frame -> pas de scintillement), cosmétique pur.
// Vague 2 (21/07) : + `victoire-confettis` (pluie de confettis), `victoire-anneau` (anneau d'étoiles
// tournant), `victoire-colonne` (colonne de lumière + étincelles), `victoire-arcenciel` (rayons
// arc-en-ciel pulsés), `victoire-royaux` (bouquet de gerbes royales en boucle). Toutes fonction de
// ageMs uniquement : les effets « continus » restent vivants même quand la victoire s'attarde (free-play).
function drawVictoryFx(ctx: CanvasRenderingContext2D, W: number, H: number, ageMs: number, kind: string): void {
  ctx.save()
  // Les trois célébrations du 30/07 vivent dans client/victoire-fx.ts : la planche de validation
  // /pages/cosmetiques.html les peint elle aussi, et importer solo.ts tirerait tout le jeu.
  if (victoireFx(ctx, W, H, ageMs, kind)) { ctx.restore(); return }
  if (kind === 'victoire-feu') {
    const COLORS = ['#ffd23f', '#ff5f57', '#5db4ff', '#b98bff', '#5fbf4f']
    // [fractionX, fractionY, délai ms] : fusées échelonnées, hautes (au-dessus du panneau central).
    const bursts: [number, number, number][] = [[0.22, 0.24, 0], [0.5, 0.16, 260], [0.78, 0.28, 520], [0.36, 0.14, 820], [0.66, 0.22, 1120]]
    for (const [fx, fy, delay] of bursts) {
      const t = ageMs - delay
      if (t < 0 || t > 900) continue
      const k = t / 900
      const cx = fx * W, cy = fy * H, r = 20 + k * 130
      ctx.globalAlpha = Math.max(0, 1 - k)
      ctx.fillStyle = COLORS[Math.floor(delay / 200) % COLORS.length]!
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2
        ctx.fillRect(cx + Math.cos(a) * r - 2, cy + Math.sin(a) * r + k * k * 40 - 2, 4, 4) // gerbe + gravité douce
      }
    }
  } else if (kind === 'victoire-pluie') {
    ctx.fillStyle = '#ffd23f'
    ctx.globalAlpha = 0.85
    for (let i = 0; i < 60; i++) {
      const x = ((i * 137) % 100) / 100 * W
      const speed = 0.25 + ((i * 53) % 100) / 100 * 0.4 // px/ms, varié mais fixe par traînée
      const y = ((ageMs * speed + i * 90) % (H + 60)) - 30
      ctx.fillRect(x, y, 3, 12) // traînée de pièce d'or qui tombe
    }
  } else if (kind === 'victoire-confettis') {
    // Confettis multicolores qui tombent en se balançant (continu tant que l'écran de victoire dure).
    const COLORS = ['#ffd23f', '#ff5f57', '#5db4ff', '#b98bff', '#5fbf4f', '#ff9ec7']
    ctx.globalAlpha = 0.9
    for (let i = 0; i < 70; i++) {
      const speed = 0.12 + ((i * 53) % 100) / 100 * 0.22 // px/ms, fixe par confetti
      const x = ((i * 137) % 100) / 100 * W + Math.sin(ageMs / 400 + i) * 14 // balancement latéral
      const y = ((ageMs * speed + i * 70) % (H + 40)) - 20
      ctx.fillStyle = COLORS[i % COLORS.length]!
      ctx.fillRect(x, y, 5, 9)
    }
  } else if (kind === 'victoire-anneau') {
    // Anneau d'étoiles qui tournoie autour du haut de l'aire, rayon qui respire, étoiles scintillantes.
    const cx = W / 2, cy = H * 0.42
    const spin = ageMs / 900
    const r = Math.min(W, H) * 0.26 + Math.sin(ageMs / 500) * 10
    ctx.fillStyle = '#ffe27a'
    ctx.globalAlpha = 0.85
    for (let i = 0; i < 12; i++) {
      const a = spin + (i / 12) * Math.PI * 2
      const sx = cx + Math.cos(a) * r, sy = cy + Math.sin(a) * r
      const s = 3 + 2 * (0.5 + 0.5 * Math.sin(ageMs / 200 + i)) // scintillement ; croix = petite étoile
      ctx.fillRect(sx - s, sy - 1, s * 2, 2)
      ctx.fillRect(sx - 1, sy - s, 2, s * 2)
    }
  } else if (kind === 'victoire-colonne') {
    // Colonne de lumière verticale au centre (dégradé qui respire) + étincelles qui montent dedans.
    const cx = W / 2
    const halfW = 34 + Math.sin(ageMs / 260) * 6 // largeur qui respire
    const grad = ctx.createLinearGradient(0, 0, 0, H)
    grad.addColorStop(0, 'rgba(255,236,140,0)')
    grad.addColorStop(0.5, 'rgba(255,236,140,0.28)')
    grad.addColorStop(1, 'rgba(255,236,140,0)')
    ctx.globalAlpha = 1
    ctx.fillStyle = grad
    ctx.fillRect(cx - halfW, 0, halfW * 2, H)
    ctx.fillStyle = '#fff6c8'
    ctx.globalAlpha = 0.8
    for (let i = 0; i < 26; i++) {
      const speed = 0.14 + ((i * 41) % 100) / 100 * 0.2
      const x = cx + (((i * 53) % 100) / 100 - 0.5) * halfW * 1.6
      const y = H - ((ageMs * speed + i * 60) % (H + 30))
      ctx.fillRect(x, y, 3, 7) // étincelle qui monte dans le faisceau
    }
  } else if (kind === 'victoire-arcenciel') {
    // Explosion arc-en-ciel (méga) : rayons multicolores qui jaillissent du centre, pulsés en continu.
    const cx = W / 2, cy = H * 0.42
    const RAINBOW = ['#ff5f57', '#ff9f43', '#ffd23f', '#5fbf4f', '#5db4ff', '#4b6bff', '#b98bff']
    const rays = 42
    const reach = Math.min(W, H) * 0.5
    for (let i = 0; i < rays; i++) {
      const a = (i / rays) * Math.PI * 2 + ageMs / 1400 // éventail qui tourne lentement
      const k = ((ageMs / 900) + i / rays) % 1 // chaque rayon a sa propre pulsation, décalée
      const len = reach * k
      ctx.globalAlpha = Math.max(0, 1 - k)
      ctx.fillStyle = RAINBOW[i % RAINBOW.length]!
      ctx.fillRect(cx + Math.cos(a) * len - 3, cy + Math.sin(a) * len - 3, 6, 6)
    }
  } else if (kind === 'victoire-royaux') {
    // Feux royaux (méga) : bouquet de gerbes or/pourpre/blanc à doubles couronnes, rejoué en boucle
    // (2400 ms) pour ne jamais laisser l'écran vide quand la victoire s'attarde (free-play).
    const COLORS = ['#ffd23f', '#b98bff', '#ffffff', '#ffb347']
    const bursts: [number, number, number][] = [[0.28, 0.22, 0], [0.5, 0.14, 220], [0.72, 0.24, 440], [0.4, 0.3, 680], [0.6, 0.18, 920], [0.5, 0.27, 1160]]
    const local = ageMs % 2400
    for (const [fx, fy, delay] of bursts) {
      const t = local - delay
      if (t < 0 || t > 900) continue
      const k = t / 900
      const cx = fx * W, cy = fy * H, r = 24 + k * 170
      ctx.globalAlpha = Math.max(0, 1 - k)
      ctx.fillStyle = COLORS[Math.floor(delay / 220) % COLORS.length]!
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2
        ctx.fillRect(cx + Math.cos(a) * r - 2, cy + Math.sin(a) * r + k * k * 50 - 2, 4, 4)               // grande gerbe + gravité
        ctx.fillRect(cx + Math.cos(a) * r * 0.58 - 1, cy + Math.sin(a) * r * 0.58 + k * k * 30 - 1, 3, 3) // couronne interne (double gerbe royale)
      }
    }
  }
  ctx.restore()
}

export function startSolo(opts: SoloOpts): SoloHandle {
  const ctx = opts.canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false
  setRunnerLooks(null) // solo : les prefs locales font foi (les looks réseau ne survivent pas ici)
  const rc: RenderCtx = { ctx, sheet: opts.sheet }
  // Free-play (20/07) : à la victoire, ne PAS enchaîner tout seul -> l'écran de victoire attend le
  // choix du joueur (rejouer / suivant). Défaut true (campagnes, niveaux uniques). Voir SoloOpts.
  const autoAdvance = opts.autoAdvance !== false
  // Éclairs de tir tazer en attente (transitoires) : remplis dans onTick sur l'event 'taze',
  // purgés (> FLASH_MS) et peints dans draw. Pas de raf/interval propre : la boucle les porte.
  const flashes: TazeFlash[] = []
  // Anim de creusage (transitoire, cosmétique) : par coureur, tick jusqu'auquel jouer la frame
  // `dig`. Rempli dans onTick sur l'event digAnim, remis à zéro à chaque swapState (le nouveau
  // niveau/respawn repart au tick 0 -> une échéance de l'ancien état ne doit pas traîner).
  const digUntil: number[] = []
  // SÉRIE DE PIÈCES (26/07, chantier « jus ») : pièces enchaînées par le coureur local. Purement
  // client (aucune entrée dans la simulation ni le hash) ; remise à zéro à chaque tentative.
  let serie: Serie = SERIE_VIDE
  // LA CHASSE (26/07) : esquives du niveau + humeur précédente de chaque moine, pour ne sonner que
  // le FRONT MONTANT du repérage. Purement client, dérivé de l'état : rien n'entre dans la sim.
  let chasse: Chasse = CHASSE_VIDE
  let humeurs: (string | null)[] = []
  // Habillage du saut trampoline (18/07, variantes aléatoires) : par coureur, [tick du rebond,
  // ampleur en su] mémorisés sur l'event bounce — même hygiène de purge que digUntil.
  const bounceAnim: [number, number][] = []
  const kb = createKeyboard()
  kb.attach()

  // Tactile : monté uniquement en mode tactile. La pause gèle la sim via state.finished
  // (le tick avance quand même : le CHRONO TOURNE pendant la pause, anti-triche assumé).
  const touch = isTouch ? createTouchControls({
    onPause() {
      if (phase === 'playing') { state.finished = true; enterPhase('paused', Infinity) }
      else if (phase === 'paused') quitToMenu()
    },
    onRestart() { restartLevel() },
    onMute() { toggleMute(); touch!.setMuted(isMuted()) },
  }) : null
  touch?.attach()
  touch?.setMuted(isMuted())
  function mergedInput(): PlayerInput {
    const a = kb.current()
    if (!touch) return a
    const b = touch.current()
    return {
      left: a.left || b.left, right: a.right || b.right, up: a.up || b.up, down: a.down || b.down,
      digL: a.digL || b.digL, digR: a.digR || b.digR, use: a.use || b.use, dash: a.dash || b.dash,
      useDir: a.useDir ?? b.useDir, // sens d'usage (#5) : clavier null -> tactile
    }
  }

  let lives = LIVES
  let index = opts.startIndex
  let tries = 0
  let phase: Phase = 'playing'
  let phaseUntil = 0
  let done = false
  // Tick figé à l'instant de la victoire : la sim continue d'incrémenter s.tick même
  // gelée (s.tick++ précède le garde `finished` dans sim.ts), donc on fige le temps
  // affiché sur l'écran de victoire au lieu de lire cur.tick qui dériverait.
  let wonTick = 0
  // Cause du dernier `death` du coureur (Tâche 2.3) : mémorisée à la mort pour l'afficher
  // sur l'écran « PERDU ! » (POURQUOI on est mort). null tant qu'aucune mort n'a eu lieu.
  let deathCause: DeathCause | null = null
  // Compteurs de quête (Lot A) : cumul HORS simulation des events du niveau EN COURS (golds/digs/
  // dashes/bounces/monksTrapped), tenus à travers les ré-essais après mort, remis à zéro à la
  // victoire (envoi au serveur) pour repartir propre au niveau suivant.
  let counters: QuestCounters = { ...ZERO_COUNTERS }
  // Fenêtre du COMBO de moines (05/08) : 2 pièges à moins d'1 s d'écart = 1 combo (avanceCombo).
  let comboFen: FenetreCombo = FENETRE_VIERGE
  // Pioches au DÉBUT du dernier essai (05/08) : « finis sans creuser » se juge sur le dernier essai,
  // pas sur le cumul du niveau — sinon un coup de pioche dans un essai raté condamnait la quête.
  let digsAuDepart = 0
  // Pas cosmétiques (Lot C T5, loot `steps`) : dernière COLONNE du coureur local + tick du dernier
  // pas joué, pour un son au changement de case (au sol), espacé d'au moins quelques ticks.
  let lastStepCol = -1
  let lastStepTick = -8
  // Départ « speed-run » (#1, 19/07, demande Claude-Alain « rien ne se passe avant que le joueur
  // agisse, pour des speed-runs propres ») : le niveau reste GELÉ (chrono à 0, moines figés) tant
  // que `started` est faux. Il s'ARME au premier ordre du joueur en phase de jeu ; chaque nouvelle
  // tentative (swapState) le désarme -> chaque essai repart d'un départ identique et contrôlé.
  let started = false
  // L'ENTRÉE EN SCÈNE (01/08). `entreeT0` = l'instant où elle a commencé, 0 = aucune en cours.
  // Elle se greffe sur le gel qui existait DÉJÀ pour les départs speed-run : tant qu'elle joue,
  // `armed()` répond faux, donc la boucle ne tique pas — chrono à zéro, moines immobiles, aucune
  // seconde offerte ni perdue. C'est ce qui permet de promettre « avant le premier instant de
  // simulation » sans toucher d'une ligne au moteur.
  let entreeT0 = 0
  function entreeEnCours(): boolean {
    return entreeT0 > 0 && performance.now() - entreeT0 < ENTREE_MS
  }
  /** Lance l'entrée si le joueur en a une ET qu'elle a un dessin. Un identifiant inconnu ne doit
   *  jamais figer la partie trois secondes sur une animation vide. */
  function lanceEntree(): void {
    entreeT0 = opts.entree && entreeDessinee(opts.entree) ? performance.now() : 0
  }
  lanceEntree()
  const isActiveInput = (i: PlayerInput): boolean =>
    i.left || i.right || i.up || i.down || i.digL || i.digR || i.use || i.dash
  function armed(): boolean {
    if (entreeEnCours()) return false // rien ne bouge pendant l'arrivée, pas même sur un appui
    if (!started && phase === 'playing' && isActiveInput(mergedInput())) started = true
    return started || phase !== 'playing' // gelé UNIQUEMENT en phase de jeu, avant le premier ordre
  }

  function newState(): GameState {
    const e = opts.levels[index]!
    // seed = 1000*index + tries : varie la trajectoire des moines à chaque tentative. monkSpeed et
    // smartMonks viennent du niveau ; absents (niveaux de test embarqués) -> 'normal'/false via createGame.
    return createGame(levelFromAscii(e.slug, e.rows), { mode: 'solo', seed: 1000 * index + tries, monksEnabled: true, monkSpeed: e.monkSpeed, smartMonks: e.smartMonks, monkPowers: e.monkPowers, dashEnabled: dashPref() })
  }
  let state = newState()

  // --- LE FANTÔME DU RECORDMAN (25/07) ---------------------------------------------------------
  // `recorder` capte l'entrée de chaque tick de TA partie ; `ghostTicks` est la course à battre,
  // cherchée au serveur en arrière-plan ; `ghost` la rejoue en parallèle, un tick pour un tick.
  // Tout est OPTIONNEL et silencieux : hors ligne, sans record, ou fantôme désactivé dans les
  // réglages, la partie se déroule exactement comme avant.
  let recorder = new GhostRecorder()
  let ghostTicks: { ticks: string; pseudo: string; timeMs: number; seed: number } | null = null
  let ghost: GhostRun | null = null
  let seed = 1000 * index + tries // la graine EFFECTIVE de la partie en cours (enregistrée avec le fantôme)

  /** Va chercher le fantôme à battre pour le niveau courant, puis l'arme s'il arrive à temps.
   *  Best-effort intégral : toute erreur (hors ligne, 204, niveau inconnu) laisse `ghost` à null. */
  function loadGhost(): void {
    ghostTicks = null
    ghost = null
    if (!ghostPref()) return
    const e = opts.levels[index]!
    const wanted = index
    apiGhost(e.slug, dashPref(), ghostRival())
      .then((g) => {
        // Le joueur a pu changer de niveau pendant la requête : on n'arme que si c'est toujours lui.
        if (!g || wanted !== index || !ghostPref()) return
        ghostTicks = g
        armGhost()
        showToast(`👻 Fantôme de ${g.pseudo} : ${formatTime(g.timeMs)}`)
      })
      .catch(() => { /* hors ligne / non connecté : aucun fantôme, aucun message */ })
  }

  /** (Re)construit le rejoueur pour la tentative EN COURS. Le fantôme rejoue avec SA graine (celle
   *  de sa propre course), pas celle de ta tentative : c'est ce qui garantit qu'il croise les mêmes
   *  moines que le jour où il a fait son temps. */
  function armGhost(): void {
    ghost = null
    if (!ghostTicks || !ghostPref()) return
    const e = opts.levels[index]!
    ghost = new GhostRun(levelFromAscii(e.slug, e.rows), ghostTicks.ticks, {
      seed: ghostTicks.seed, dashEnabled: dashPref(),
      monkSpeed: e.monkSpeed, smartMonks: e.smartMonks, monkPowers: e.monkPowers,
    })
  }

  /** L'entrée du tick : c'est LE point unique que le moteur consomme (client/loop.ts), donc le seul
   *  endroit honnête où enregistrer. On y fait aussi avancer le fantôme d'un tick, pour qu'il reste
   *  en phase avec la partie même quand des frames sont sautées. */
  function tickInput(): PlayerInput {
    const i = mergedInput()
    if (armed()) { recorder.push(i); ghost?.step() }
    return i
  }

  // ARRÊT SUR IMAGE : on réutilise le gel déjà en place (`running`), dont le temps figé est JETÉ —
  // donc ni le chrono (compté en ticks) ni le fantôme (tick pour tick) ne dérivent. Solo seulement :
  // l'en-ligne ne passe pas de `running`, sa cadence appartient au serveur.
  const loopOpts = { state, input: tickInput, onTick, draw, running: () => armed() && !hitstopActif(performance.now()) }
  let loop: LoopHandle

  function enterPhase(p: Phase, durMs: number): void {
    phase = p
    phaseUntil = performance.now() + durMs
  }
  function swapState(): void {
    seed = 1000 * index + tries // suit newState() : la graine enregistrée doit être celle jouée
    state = newState()
    loopOpts.state = state
    // Chaque tentative repart d'un enregistrement vierge et d'un fantôme au départ : sinon on
    // enregistrerait la course précédente en préambule, et le fantôme serait déjà à mi-carte.
    recorder = new GhostRecorder()
    armGhost()
    started = false // #1 : chaque nouvelle tentative (respawn OU niveau suivant) repart GELÉE (départ propre)
    digUntil.length = 0 // le nouvel état repart au tick 0 : purge les échéances d'anim de l'ancien
    resetHitstop() // une échéance qui traîne gèlerait le début du niveau suivant
    serie = SERIE_VIDE
    chasse = CHASSE_VIDE
    humeurs = []
    bounceAnim.length = 0
    lastStepCol = -1; lastStepTick = -8 // pas cosmétiques : le nouveau niveau repart au tick 0
    startAmbiance(opts.levels[index]!.world) // ambiance sonore du biome du niveau courant
    startMusic(opts.levels[index]!.world) // musique de fond du biome (idempotent : respawn = même biome, le groove continue)
  }
  // Recommencer le niveau courant (#2, 19/07, « bouton recommencer à côté de pause ») : recharge le
  // MÊME niveau (seed inchangé -> parcours des moines identique, idéal pour s'entraîner en speed-run)
  // et le re-gèle (#1). Ni vie ni « tentative » consommée : c'est un choix du joueur, pas une mort.
  function restartLevel(): void {
    // En jeu ou en pause : recommencer (bouton 🔄 / touche R). Sur l'ÉCRAN DE VICTOIRE en free-play
    // (autoAdvance faux) : rejouer le même niveau pour enchaîner un speed-run. En campagne (défaut),
    // la victoire enchaîne toute seule -> pas de rejeu depuis la victoire (ce garde l'interdit).
    if (phase !== 'playing' && phase !== 'paused' && !(phase === 'won-level' && !autoAdvance)) return
    swapState()
    phase = 'playing'
  }
  // Passer au niveau suivant (ou écran final au dernier). Extrait pour être partagé par le timer
  // d'enchaînement auto (campagnes) ET le choix manuel →/tap en free-play. tries RAZ = nouveau niveau.
  function nextLevel(): void {
    if (index >= opts.levels.length - 1) enterPhase('all-done', END_MS)
    else { index += 1; tries = 0; swapState(); loadGhost(); lanceEntree(); phase = 'playing' }
  }
  function teardown(): void {
    if (done) return
    done = true
    loop.stop()
    stopAmbiance() // coupe l'ambiance sonore de fond en quittant la partie
    stopMusic() // coupe la musique de fond (arrête + déconnecte tous ses nœuds : zéro fuite)
    kb.detach()
    touch?.detach()
    removeEventListener('keydown', onKey)
    opts.canvas.removeEventListener('click', onClick)
    closeEmoteWheel() // la roue est un overlay DOM : elle ne doit jamais survivre à la partie
    stopEmote()
  }
  function quitToMenu(): void {
    if (done) return
    teardown()
    opts.onQuit()
  }

  function onTick(events: GameEvent[]): void {
    // LE JUS (26/07). La série se calcule AVANT le son : c'est elle qui donne sa hauteur au blip
    // d'or. Les trois déclencheurs ci-dessous sont ce que la comparaison de deux états ne peut pas
    // voir (updateShake ne connaît que l'état), d'où le passage par les événements.
    const orRamasse = events.filter((e) => e.type === 'gold' && e.by === 'runner').length
    serie = serieSuivante(serie, state.tick, orRamasse)
    const maintenant = performance.now()
    for (const e of events) {
      if (e.type === 'dig') bumpShake(2, state.tick)                       // le geste le plus répété du jeu : il doit peser
      else if (e.type === 'monkTrapped') {                                  // le meilleur moment de la partie
        bumpShake(5, state.tick)
        declencheHitstop(HITSTOP_MS.moinePiege, maintenant)
      } else if (e.type === 'exitRevealed') {                               // la dernière pièce : la sortie s'ouvre
        bumpShake(3, state.tick)
        declencheHitstop(HITSTOP_MS.dernierePiece, maintenant)
      } else if (e.type === 'malletHit') {                                  // coup de maillet encaissé (05/08) : il PÈSE
        declencheHitstop(HITSTOP_MS.malletHit, maintenant)
      }
    }
    playEvents(events, serie.n)
    // LA CHASSE. Deux choses que la simulation ne dit pas, et qu'on dérive de l'état :
    //   · le SON du repérage, sur le front montant seulement (un moine qui te voit en continu
    //     hurlerait trente fois par seconde) ;
    //   · les ESQUIVES, comptées à la SORTIE du danger — récompenser l'entrée féliciterait le
    //     joueur de s'être mis en péril.
    if (state.smartMonks) {
      state.monks.forEach((m, i) => {
        const dispo = m.respawnAt === 0 && !m.dormant && m.trappedCell < 0 && m.stunUntil <= state.tick && m.work === 0
        const h = dispo ? monkMood(state, m) : null
        if (vientDeReperer(humeurs[i] ?? null, h)) sonRepere()
        humeurs[i] = h
      })
    }
    const moi = state.runners[0]
    if (moi) {
      let dist = Infinity
      for (const m of state.monks) {
        if (m.respawnAt !== 0 || m.dormant) continue
        dist = Math.min(dist, Math.max(Math.abs(m.x - moi.x), Math.abs(m.y - moi.y)))
      }
      chasse = chasseSuivante(chasse, dist, moi.alive)
    }
    const monksAvant = counters.monksTrapped
    counters = tallyEvents(counters, events, 0) // cumul des compteurs de quête (joueur local = index 0 en solo)
    // Combo : les pièges de CE tick entrent dans la fenêtre d'une seconde (05/08).
    const av = avanceCombo(comboFen, counters.monksTrapped - monksAvant, state.tick)
    comboFen = av.fen
    counters.monkCombos += av.combos
    touch?.setItem(state.runners[0]!.item) // icône du bouton OBJET = objet équipé (16/07)
    touch?.setOutil(state.runners[0]!.maillet) // 06/08 : les deux boutons d'action passent de la pioche au maillet
    touch?.setNage(estImmerge(state, state.runners[0]!)) // 06/08 : la diagonale, dans l'eau seulement
    // Pas cosmétiques (Lot C T5) : son discret au CHANGEMENT DE CASE du coureur local, au sol,
    // en jeu, si un loot `steps` est équipé. stepSound respecte le muet et ne fait rien sans loot
    // (cache vide hors ligne) -> additif, jamais bloquant. On tient la colonne à jour en continu
    // (même en l'air) pour ne pas cracher une rafale de pas au retour au sol.
    const rStep = state.runners[0]
    if (rStep) {
      const col = Math.floor(rStep.x / SUB)
      if (phase === 'playing' && rStep.alive && !rStep.falling && !rStep.onLadder && !rStep.onRope
        && lastStepCol >= 0 && col !== lastStepCol && state.tick - lastStepTick >= 4) {
        stepSound(equipped('steps')); lastStepTick = state.tick // pas de son avant une colonne de référence (anti-pas au spawn)
      }
      lastStepCol = col
    }
    // Chaque tir tazer arme un éclair transitoire (from/to en cases) horodaté ; draw le purge.
    for (const e of events) if (e.type === 'taze') flashes.push({ from: e.from, to: e.to, bornAt: performance.now() })
    // Un creusage réussi arme la frame `dig` de CE coureur pour DIG_ANIM_TICKS ticks (échéance en ticks de sim).
    for (const e of events) if (e.type === 'digAnim') digUntil[e.runner] = state.tick + DIG_ANIM_TICKS
    // Rebond : mémorise [tick, ampleur] pour l'habillage du saut (4 variantes aléatoires).
    // Variante « propulseur » (3) sur un VRAI trampoline (>= 24 su) : ping programmé à l'apex.
    for (const e of events) if (e.type === 'bounce') {
      const rn = state.runners[e.runner]
      if (rn) {
        bounceAnim[e.runner] = [state.tick, rn.bounce]
        if (rn.bounce >= 24 && bounceVariant(state.tick, e.runner) === 3) apexPing(rn.bounce / 2 / 30)
      }
    }
    // Mort : capte la cause du dernier `death` du coureur (index 0) pour l'écran de perte.
    // On ne l'écrase que sur une vraie nouvelle mort (le respawn garde la dernière, jamais affichée).
    const dc = lastDeathCause(events, 0)
    if (dc) deathCause = dc
    if (phase === 'playing') {
      if (!state.runners[0]!.alive) {
        // CHECKPOINT (16/07 soir) : mort avec un VER actif (le moteur a programmé la
        // réanimation -> respawnAt > 0) et des vies en réserve : la partie CONTINUE
        // (or ramassé conservé, moines vivants), on décompte la vie et on affiche le
        // bandeau. Dernière vie ou pas de ver : chemin historique (restart / game-over).
        if (state.runners[0]!.respawnAt > 0 && lives > 1) { lives -= 1; tries += 1; phase = 'reviving' }
        else { state.finished = true; enterPhase('dying', DYING_MS) }
      }
      else if (state.won) {
        // checkWin a déjà posé finished. On fige le temps et on remonte le score : timeMs
        // arrondi et >= 1 (le serveur valide 1..3_600_000), deaths = morts sur CE niveau
        // (tries, remis à 0 au passage au niveau suivant).
        wonTick = state.tick
        const timeMs = Math.max(1, Math.round(wonTick * 1000 / 30))
        opts.onLevelWon?.(opts.levels[index]!.slug, timeMs, tries, { dash: state.dashEnabled, torch: torchPref() })
        // FANTÔME (25/07) : on dépose la course APRÈS le score (l'ordre compte — le serveur n'accepte
        // le fantôme que s'il correspond au record personnel, donc `scores` doit déjà porter le
        // nouveau temps). Tirer-et-oublier : le serveur ignore en silence une course plus lente.
        if (recorder.usable) {
          void apiPutGhost(opts.levels[index]!.slug, state.dashEnabled, torchPref(), timeMs, seed, encodeInputs(recorder.recorded))
        }
        // Économie / quêtes (Lot A) : POST des compteurs + finish, TIRER-ET-OUBLIER (onTick est
        // synchrone) et JAMAIS bloquant (.catch avale l'échec réseau / le 401 hors connexion).
        // finishes:1 est OBLIGATOIRE (le serveur ne le dérive pas, contrairement à finishesNoDeath).
        // beatRecord:false : le solo ne connaît pas le record perso (délégué à onLevelWon->main.ts),
        // on ne le DEVINE pas. Le payload fige un snapshot des compteurs avant leur RAZ.
        // Dérivés HORS-sim (Vague 1, 20/07), connus ICI seul (tick de victoire, morts, compteurs) :
        //   finishesFast   = niveau bouclé sous FAST_FINISH_MS (vitesse) ;
        //   finishesNoDig  = terminé sans un seul coup de pioche (counters.digs cumulé sur les essais) ;
        //   finishesFlawless = rapide ET sans mourir (tries === 0). Le serveur ne les dérive pas : il
        // les lit tels quels dans `counters` (comme finishes) et n'incrémente que les quêtes actives.
        const fast = timeMs < FAST_FINISH_MS
        const payload = {
          counters: {
            ...counters,
            finishes: 1,
            finishesFast: fast ? 1 : 0,
            // « Sans creuser » = pas UN coup de pioche depuis le DERNIER départ (05/08) : le cumul
            // du niveau traverse les essais ratés, digsAuDepart marque où le dernier a commencé.
            finishesNoDig: counters.digs === digsAuDepart ? 1 : 0,
            finishesFlawless: tries === 0 && fast ? 1 : 0,
          },
          finished: true, noDeath: tries === 0, beatRecord: false,
        }
        counters = { ...ZERO_COUNTERS } // RAZ pour le niveau suivant
        digsAuDepart = 0
        comboFen = FENETRE_VIERGE
        apiQuestProgress(payload).then((res) => { showQuestToasts(res.completed); showDailyToasts(res.daily) }).catch(() => { /* hors ligne / non connecté : silencieux */ })
        enterPhase('won-level', WON_MS)
      }
      return
    }
    // Pause : ne rien faire au timer (phaseUntil = Infinity suffirait, mais soyons clairs).
    if (phase === 'paused') return
    // Réanimation au ver (16/07 soir) : la sim TOURNE (pas de finished), on attend la
    // résurrection moteur. AVANT le check phaseUntil : cette phase n'a pas de minuterie.
    if (phase === 'reviving') { if (state.runners[0]!.alive) phase = 'playing'; return }
    if (performance.now() < phaseUntil) return
    if (phase === 'dying') {
      lives -= 1
      if (lives <= 0) enterPhase('game-over', END_MS)
      else {
        tries += 1; swapState(); phase = 'playing'
        digsAuDepart = counters.digs   // le « sans creuser » repart d'ici (05/08)
      }
    } else if (phase === 'won-level') {
      // Campagnes / défaut : enchaînement AUTOMATIQUE au niveau suivant quand le court écran expire.
      // Free-play : on N'AVANCE PAS au timer -> l'écran de victoire reste jusqu'au choix du joueur
      // (R/🔄 rejoue, →/tap passe au suivant). Le bloc se ré-évalue à chaque tick : no-op inoffensif.
      if (autoAdvance) nextLevel()
    } else { // game-over | all-done : fin de partie
      quitToMenu()
    }
  }

  // Décor (couche cosmétique) du niveau courant, parsé UNE fois par niveau (memo sur la chaîne :
  // re-parse seulement au changement de niveau), jamais dans la simulation.
  let decorStr = '\x00'
  let decorCache: DecorGrid = decorGrid('')
  function levelDecor(): DecorGrid {
    const s = opts.levels[index]!.decor ?? ''
    if (s !== decorStr) { decorStr = s; decorCache = decorGrid(s) }
    return decorCache
  }

  /**
   * Peint l'arrivée par-dessus le niveau, dans l'aire de JEU seulement (le bandeau du bas garde
   * sa place : un voile qui mange le HUD se lit comme un bug d'affichage).
   *
   * Le héros arrive là où il va vraiment démarrer — `posEcranCoureur` convertit sa case en pixels
   * écran, zoom et caméra compris — et il porte SES cosmétiques. `solide` dit au wagonnet où le
   * sol est réellement plein : sur une carte trouée à hauteur du spawn, la voie s'interrompt au
   * lieu de flotter au-dessus du vide.
   */
  function peintArrivee(cur: GameState, cam: { camX: number; camY: number }): void {
    const r0 = cur.runners[0]
    if (!r0 || !opts.entree) return
    const p = posEcranCoureur(r0, cam)
    const rowSol = Math.min(ROWS - 1, Math.floor(r0.y / SUB) + 1)   // la rangée SOUS ses pieds
    const zoom = p.ySol > 0 ? (p.ySol / ((r0.y / SUB + 1) * TS - cam.camY)) : 1
    const solide = (xEcran: number): boolean => {
      const col = Math.floor((xEcran / zoom + cam.camX) / TS)
      if (col < 0 || col >= cur.cols) return false
      return isSolid(cur.terrain[rowSol * cur.cols + col] ?? 0)
    }
    ctx.save()
    ctx.beginPath(); ctx.rect(0, 0, opts.canvas.width, ROWS * TS); ctx.clip()
    peintEntree(ctx, opts.canvas.width, ROWS * TS, performance.now() - entreeT0, opts.entree, {
      cx: p.cx, ySol: p.ySol, hue: hueIndex(avatarHue()),
      outfit: equipped('outfit') ?? '', hat: equipped('hat') ?? '', solide,
    })
    ctx.restore()
  }
  function draw(prev: GameState | null, cur: GameState, alpha: number): void {
    // ENTRÉE EN SCÈNE : pendant l'arrivée, les coureurs ne sont pas dessinés — c'est l'animation
    // qui peint le héros, et elle le place ailleurs qu'à son spawn. Sans ça, on en verrait deux.
    const arrive = entreeEnCours()
    const cam = render(rc, prev, cur, alpha, opts.levels[index]!.world, levelDecor(), digUntil, 0, bounceAnim, ghost?.frame() ?? null, arrive) // solo : caméra suit le coureur 0
    if (arrive) peintArrivee(cur, cam)
    // Éclairs de tazer : purge des transitoires expirés puis rendu par-dessus le terrain,
    // sous le HUD et les panneaux de transition.
    const now = performance.now()
    while (flashes.length > 0 && now - flashes[0]!.bornAt > FLASH_MS) flashes.shift()
    drawTazeFlashes(rc, flashes, now, cur.cols, cam)
    const r0 = cur.runners[0]!
    renderHud(rc, cur, {
      lives, levelName: opts.levels[index]!.title, timeMs: cur.tick * 1000 / 30, muted: isMuted(), touchMode: isTouch,
      // Objet porté -> bandeau (16/07 soir) ; charges affichées pour tazer/corde seulement.
      item: r0.item ? { kind: r0.item, count: r0.item === 'tazer' || r0.item === 'rope' ? r0.charges : 0 } : null,
      checkpoint: r0.checkpoint >= 0, // tête de ver au bandeau : un point de réanimation est actif
      // Qui est le coureur translucide, et en combien de temps il a fait la carte (26/07).
      ghost: ghost && ghostTicks ? { pseudo: ghostTicks.pseudo, timeMs: ghostTicks.timeMs } : null,
    })
    // LA SÉRIE, en grand au-dessus de l'aire de jeu (26/07). Volontairement HORS du bandeau : il
    // est déjà plein, et un compteur de série doit sauter aux yeux à l'instant où il monte, pas se
    // lire dans une ligne de texte. Il « pop » à chaque pièce puis s'efface avec la série.
    if (serieVisible(serie)) {
      const age = cur.tick - serie.dernier
      if (age <= SERIE_TICKS) {
        const k = Math.max(0, 1 - age / SERIE_TICKS)
        const pop = 1 + 0.4 * Math.max(0, 1 - age / 4)
        ctx.save()
        ctx.globalAlpha = 0.30 + 0.70 * k
        ctx.translate(opts.canvas.width / 2, 78)
        ctx.scale(pop, pop)
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
        ctx.font = 'bold 42px ui-monospace, Menlo, monospace'
        ctx.fillStyle = 'rgba(18,12,4,0.6)'
        ctx.fillText(`×${serie.n}`, 2, 3)
        ctx.fillStyle = '#ffd23f'
        ctx.fillText(`×${serie.n}`, 0, 0)
        ctx.font = 'bold 13px ui-monospace, Menlo, monospace'
        ctx.fillStyle = '#f0e9d2'
        ctx.fillText('SÉRIE', 0, 28)
        ctx.restore()
      }
    }
    if (phase === 'dying') drawCenterScreen(ctx, 'PERDU !', `${causeLabel(deathCause)} · Vies restantes : ${lives - 1}`)
    else if (phase === 'reviving') drawBanner(ctx, `${causeLabel(deathCause)} · Le ver te ranime…`) // partie VIVANTE derrière : bandeau léger, pas de voile
    else if (phase === 'game-over') drawCenterScreen(ctx, 'GAME OVER', isTouch ? "Touche l'écran" : 'Appuie sur Entrée')
    else if (phase === 'won-level') {
      const wonAge = now - (phaseUntil - WON_MS) // flash doré bref à l'instant de la victoire
      if (wonAge < 380) {
        ctx.save(); ctx.globalAlpha = 0.5 * (1 - wonAge / 380)
        const g = ctx.createRadialGradient(opts.canvas.width / 2, opts.canvas.height / 2, 0, opts.canvas.width / 2, opts.canvas.height / 2, opts.canvas.width * 0.6)
        g.addColorStop(0, '#ffe98a'); g.addColorStop(1, 'rgba(255,233,138,0)')
        ctx.fillStyle = g; ctx.fillRect(0, 0, opts.canvas.width, opts.canvas.height); ctx.restore()
      }
      // La meilleure série du niveau s'affiche À CÔTÉ du temps : c'est la seconde chose qu'on
      // peut refaire mieux, et elle ne dépend pas du chrono. Tue dès qu'il n'y a rien à dire.
      drawCenterScreen(ctx, 'NIVEAU RÉUSSI !',
        `Temps : ${formatTime(wonTick * 1000 / 30)}`
        + (serie.record >= SERIE_MIN ? `  ·  Meilleure série : ×${serie.record}` : '')
        + (chasse.esquives > 0 ? `  ·  Esquives : ${chasse.esquives}` : ''))
      // Effet de victoire cosmétique (Lot C T5) : feu d'artifice / pluie d'or PAR-DESSUS l'écran.
      const vic = equipped('victory')
      if (vic) drawVictoryFx(ctx, opts.canvas.width, opts.canvas.height, wonAge, vic)
      // Free-play (20/07) : l'écran attend un choix -> invite sous le panneau. En campagne (autoAdvance)
      // l'enchaînement est automatique, aucune invite. « terminer » au dernier niveau de la liste.
      if (!autoAdvance) {
        const last = index >= opts.levels.length - 1
        const hint = isTouch
          ? (last ? '🔄 rejouer   ·   touche l\'écran pour terminer' : '🔄 rejouer   ·   touche l\'écran pour continuer')
          : (last ? 'R : rejouer   ·   → : terminer' : 'R : rejouer   ·   → : niveau suivant')
        ctx.save()
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
        ctx.font = 'bold 24px ui-monospace, Menlo, monospace'
        ctx.fillStyle = '#ffd93d'
        ctx.fillText(hint, opts.canvas.width / 2, opts.canvas.height / 2 + 128)
        ctx.restore()
      }
    }
    else if (phase === 'all-done') drawCenterScreen(ctx, 'BRAVO !', 'Tu as fini les niveaux de test')
    else if (phase === 'paused') drawCenterScreen(ctx, 'PAUSE', "Touche l'écran pour continuer (le chrono tourne)")
    // Départ gelé (#1) : tant que le joueur n'a pas démarré, on l'invite à partir (le niveau est
    // figé derrière, pleinement visible, chrono à 0 -> speed-run propre).
    else if (phase === 'playing' && !started) drawBanner(ctx, isTouch ? '▶ Touche pour partir' : '▶ Appuie pour partir')
    // Fondu d'entrée : un niveau (ou un respawn) démarre au tick 0 -> voile noir qui s'estompe sur
    // FADE_TICKS ticks. Conditionné à `started` : PENDANT le gel (tick figé à 0) il peindrait un écran
    // noir opaque et masquerait le niveau ; on ne le joue donc qu'une fois le départ donné. Cosmétique.
    if (cur.tick < FADE_TICKS && started) { ctx.save(); ctx.globalAlpha = 1 - cur.tick / FADE_TICKS; ctx.fillStyle = '#000'; ctx.fillRect(0, 0, opts.canvas.width, opts.canvas.height); ctx.restore() }
  }

  function onKey(e: KeyboardEvent): void {
    // EMOTES (26/07) : en partie, SEULEMENT les raccourcis directs 1 à 8. Pas de roue ici — c'est
    // un menu DOM, et pendant qu'on le parcourt la simulation continue de tourner : un moine aurait
    // tout le temps de venir. La roue vit dans la Mine, où la scène est gelée et sans danger.
    if (phase === 'playing' && /^Digit[1-8]$/.test(e.code) && handleEmoteKey(e.code)) { e.preventDefault(); return }
    if (e.code === 'Escape' && (phase === 'playing' || phase === 'paused')) { e.preventDefault(); quitToMenu() }
    else if (e.code === 'KeyR' && (phase === 'playing' || phase === 'paused' || (phase === 'won-level' && !autoAdvance))) { e.preventDefault(); restartLevel() } // #2 recommencer en jeu / rejouer depuis la victoire (free-play)
    else if (phase === 'won-level' && !autoAdvance && (e.code === 'ArrowRight' || e.code === 'Enter' || e.code === 'NumpadEnter')) { e.preventDefault(); nextLevel() } // free-play : passer au niveau suivant
    else if ((e.code === 'Enter' || e.code === 'NumpadEnter') && (phase === 'game-over' || phase === 'all-done')) {
      e.preventDefault(); quitToMenu()
    }
  }

  // Tactile : un tap sur l'écran reprend la partie en pause, et remplace Entrée sur
  // les écrans de fin (dying et won-level avancent déjà toutes seules au timer).
  function onClick(e: MouseEvent): void {
    if (phase === 'paused') { e.preventDefault(); state.finished = false; phase = 'playing'; return }
    // Free-play : un tap/clic sur l'écran de victoire passe au niveau suivant (le bouton 🔄 rejoue).
    if (phase === 'won-level' && !autoAdvance) { e.preventDefault(); nextLevel(); return }
    if (phase === 'game-over' || phase === 'all-done') { e.preventDefault(); quitToMenu() }
  }

  addEventListener('keydown', onKey)
  opts.canvas.addEventListener('click', onClick)
  startAmbiance(opts.levels[index]!.world) // ambiance sonore du 1er niveau
  startMusic(opts.levels[index]!.world) // musique de fond du 1er niveau
  loadGhost() // fantôme à battre sur le 1er niveau (asynchrone, jamais bloquant)
  loop = startLoop(loopOpts)
  return { stop: teardown }
}
